/**
 * AI Summary Cache Service
 * 
 * This service provides a caching layer for AI-generated summaries.
 * Currently uses localStorage, but is designed for easy transition to database API calls.
 * 
 * TRANSITION GUIDE FOR DEVELOPERS:
 * 1. Replace localStorage.getItem() with API GET calls in getCachedSummary()
 * 2. Replace localStorage.setItem() with API POST calls in cacheSummary()
 * 3. Replace localStorage.removeItem() with API DELETE calls in clearCache()
 * 4. The data structure remains the same for both localStorage and database
 */

class AISummaryCacheService {
  constructor() {
    this.CACHE_PREFIX = 'ai_summary_';
    this.CACHE_VERSION = 'v1';
    this.MAX_CACHE_AGE_DAYS = 30; // Cache expires after 30 days
  }

  /**
   * Generate a unique cache key for a summary
   * @param {string} studentId - Student identifier (e.g., "student_STU0001178594")
   * @param {string} testId - Test identifier (e.g., "68259b7ffefcf4e8650e219b")
   * @param {string} skillType - Type of skill analysis (e.g., "score_insights", "accuracy", "speed")
   * @param {string} modelType - AI model used (e.g., "deepseek", "openai", "gemini")
   * @returns {string} Cache key
   */
  _generateCacheKey(studentId, testId, skillType, modelType) {
    return `${this.CACHE_PREFIX}${this.CACHE_VERSION}_${studentId}_${testId}_${skillType}_${modelType}`;
  }

  /**
   * Get cached summary from localStorage
   * 
   * FOR DATABASE TRANSITION:
   * Replace this with: 
   * const response = await axios.get(`${API_BASE_URL}/summary/cache`, { params: { studentId, testId, skillType, modelType } });
   * return response.data;
   * 
   * @param {string} studentId - Student identifier
   * @param {string} testId - Test identifier
   * @param {string} skillType - Type of skill analysis
   * @param {string} modelType - AI model used
   * @returns {Object|null} Cached summary object or null if not found/expired
   */
  getCachedSummary(studentId, testId, skillType, modelType) {
    try {
      const cacheKey = this._generateCacheKey(studentId, testId, skillType, modelType);
      const cachedData = localStorage.getItem(cacheKey);
      
      if (!cachedData) {
        console.log(`📦 AISummaryCache: No cache found for ${skillType} (${modelType})`);
        return null;
      }

      const parsedData = JSON.parse(cachedData);
      
      // Check if cache is expired
      if (this._isCacheExpired(parsedData.timestamp)) {
        console.log(`⏰ AISummaryCache: Cache expired for ${skillType} (${modelType})`);
        this.clearCache(studentId, testId, skillType, modelType);
        return null;
      }

      console.log(`✅ AISummaryCache: Cache hit for ${skillType} (${modelType})`);
      return parsedData;
      
    } catch (error) {
      console.error('❌ AISummaryCache: Error retrieving from cache:', error);
      return null;
    }
  }

  /**
   * Cache a generated summary to localStorage
   * 
   * FOR DATABASE TRANSITION:
   * Replace this with:
   * await axios.post(`${API_BASE_URL}/summary/cache`, { studentId, testId, skillType, modelType, content, metadata });
   * 
   * @param {string} studentId - Student identifier
   * @param {string} testId - Test identifier
   * @param {string} skillType - Type of skill analysis
   * @param {string} modelType - AI model used
   * @param {string} content - The generated summary content
   * @param {Object} metadata - Additional metadata (optional)
   * @returns {boolean} Success status
   */
  cacheSummary(studentId, testId, skillType, modelType, content, metadata = {}) {
    try {
      const cacheKey = this._generateCacheKey(studentId, testId, skillType, modelType);
      
      const cacheData = {
        studentId,
        testId,
        skillType,
        modelType,
        content,
        timestamp: new Date().toISOString(),
        version: this.CACHE_VERSION,
        metadata: {
          ...metadata,
          cachedAt: new Date().toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })
        }
      };

      localStorage.setItem(cacheKey, JSON.stringify(cacheData));
      console.log(`💾 AISummaryCache: Cached summary for ${skillType} (${modelType})`);
      
      // Also update the cache index for tracking
      this._updateCacheIndex(studentId, testId, skillType, modelType);
      
      return true;
      
    } catch (error) {
      console.error('❌ AISummaryCache: Error saving to cache:', error);
      return false;
    }
  }

  /**
   * Clear specific cache entry
   * 
   * FOR DATABASE TRANSITION:
   * Replace this with:
   * await axios.delete(`${API_BASE_URL}/summary/cache`, { params: { studentId, testId, skillType, modelType } });
   * 
   * @param {string} studentId - Student identifier
   * @param {string} testId - Test identifier
   * @param {string} skillType - Type of skill analysis
   * @param {string} modelType - AI model used
   */
  clearCache(studentId, testId, skillType, modelType) {
    try {
      const cacheKey = this._generateCacheKey(studentId, testId, skillType, modelType);
      localStorage.removeItem(cacheKey);
      console.log(`🗑️ AISummaryCache: Cleared cache for ${skillType} (${modelType})`);
    } catch (error) {
      console.error('❌ AISummaryCache: Error clearing cache:', error);
    }
  }

  /**
   * Clear all cached summaries for a specific student
   * Useful when student data is updated
   * 
   * @param {string} studentId - Student identifier
   */
  clearStudentCache(studentId) {
    try {
      const keys = Object.keys(localStorage);
      const studentKeys = keys.filter(key => 
        key.startsWith(this.CACHE_PREFIX) && key.includes(studentId)
      );
      
      studentKeys.forEach(key => localStorage.removeItem(key));
      console.log(`🗑️ AISummaryCache: Cleared ${studentKeys.length} cache entries for student ${studentId}`);
      
    } catch (error) {
      console.error('❌ AISummaryCache: Error clearing student cache:', error);
    }
  }

  /**
   * Clear all expired cache entries
   * Should be called periodically (e.g., on app startup)
   */
  clearExpiredCache() {
    try {
      const keys = Object.keys(localStorage);
      const cacheKeys = keys.filter(key => key.startsWith(this.CACHE_PREFIX));
      
      let clearedCount = 0;
      cacheKeys.forEach(key => {
        try {
          const data = JSON.parse(localStorage.getItem(key));
          if (this._isCacheExpired(data.timestamp)) {
            localStorage.removeItem(key);
            clearedCount++;
          }
        } catch (e) {
          // Invalid cache entry, remove it
          localStorage.removeItem(key);
          clearedCount++;
        }
      });
      
      if (clearedCount > 0) {
        console.log(`🗑️ AISummaryCache: Cleared ${clearedCount} expired cache entries`);
      }
      
    } catch (error) {
      console.error('❌ AISummaryCache: Error clearing expired cache:', error);
    }
  }

  /**
   * Get cache statistics
   * Useful for monitoring and debugging
   * 
   * @returns {Object} Cache statistics
   */
  getCacheStats() {
    try {
      const keys = Object.keys(localStorage);
      const cacheKeys = keys.filter(key => key.startsWith(this.CACHE_PREFIX));
      
      const stats = {
        totalEntries: cacheKeys.length,
        totalSize: 0,
        bySkillType: {},
        byModelType: {},
        oldestEntry: null,
        newestEntry: null
      };

      cacheKeys.forEach(key => {
        const data = localStorage.getItem(key);
        stats.totalSize += data.length;
        
        try {
          const parsed = JSON.parse(data);
          
          // Count by skill type
          stats.bySkillType[parsed.skillType] = (stats.bySkillType[parsed.skillType] || 0) + 1;
          
          // Count by model type
          stats.byModelType[parsed.modelType] = (stats.byModelType[parsed.modelType] || 0) + 1;
          
          // Track oldest and newest
          const timestamp = new Date(parsed.timestamp);
          if (!stats.oldestEntry || timestamp < new Date(stats.oldestEntry)) {
            stats.oldestEntry = parsed.timestamp;
          }
          if (!stats.newestEntry || timestamp > new Date(stats.newestEntry)) {
            stats.newestEntry = parsed.timestamp;
          }
        } catch (e) {
          // Skip invalid entries
        }
      });

      stats.totalSizeKB = (stats.totalSize / 1024).toFixed(2);
      
      return stats;
      
    } catch (error) {
      console.error('❌ AISummaryCache: Error getting cache stats:', error);
      return null;
    }
  }

  /**
   * Check if cache entry is expired
   * @private
   */
  _isCacheExpired(timestamp) {
    if (!timestamp) return true;
    
    const cacheDate = new Date(timestamp);
    const now = new Date();
    const diffDays = (now - cacheDate) / (1000 * 60 * 60 * 24);
    
    return diffDays > this.MAX_CACHE_AGE_DAYS;
  }

  /**
   * Update cache index for tracking all cached items
   * @private
   */
  _updateCacheIndex(studentId, testId, skillType, modelType) {
    try {
      const indexKey = `${this.CACHE_PREFIX}index`;
      const indexData = JSON.parse(localStorage.getItem(indexKey) || '[]');
      
      const entry = {
        studentId,
        testId,
        skillType,
        modelType,
        cachedAt: new Date().toISOString()
      };
      
      // Remove old entry if exists
      const filtered = indexData.filter(item => 
        !(item.studentId === studentId && 
          item.testId === testId && 
          item.skillType === skillType && 
          item.modelType === modelType)
      );
      
      filtered.push(entry);
      localStorage.setItem(indexKey, JSON.stringify(filtered));
      
    } catch (error) {
      console.error('❌ AISummaryCache: Error updating cache index:', error);
    }
  }
}

// Export singleton instance
const aiSummaryCacheService = new AISummaryCacheService();

// Clear expired cache on service initialization
aiSummaryCacheService.clearExpiredCache();

export default aiSummaryCacheService;
